import { API } from '../Api';

// "Hỏi đáp AI trên live": người xem trả Ca gửi câu hỏi cho streamer đang live, AI viết câu
// trả lời, streamer duyệt hai bước (duyệt rồi phát lên live) trước khi hiện trên phần chèn
// riêng. Chỉ trừ Ca lúc "Phát lên live", đi lại đường DonateRequest có sẵn.

export type AiQuestionStatus =
  | 'Pending'
  | 'Answering'
  | 'Ready'
  | 'Played'
  | 'Rejected'
  | 'Expired'
  | 'Failed';

/** Câu hỏi của chính người xem — `answer` chỉ có giá trị khi đã `Played`. */
export interface AiQuestionMine {
  id: string;
  question: string;
  status: AiQuestionStatus;
  answer: string | null;
  createdAt: string;
}

/** Cài đặt công khai cho khối người xem — không có `overlayKey`. */
export interface PublicAiQuestionSetting {
  enabled: boolean;
  priceCa: number;
}

/** Cài đặt đầy đủ cho streamer — chỉ trả cho chính streamer (khu quản lý). */
export interface AiQuestionSetting {
  enabled: boolean;
  priceCa: number;
  overlayKey: string;
}

/** 1 câu hỏi trong hàng chờ quản lý của streamer — `askerName` luôn là tên thật, `isAnonymous`
 * để FE tự hiện nhãn "ẩn danh" cạnh tên (không ẩn danh ở khu quản lý, chỉ ẩn ở overlay công khai). */
export interface AiQuestionQueueItem {
  id: string;
  askerName: string;
  isAnonymous: boolean;
  question: string;
  answer: string | null;
  status: AiQuestionStatus;
  priceCa: number;
  rewriteCount: number;
  failReason: string | null;
  createdAt: string;
  readyAt: string | null;
  playedAt: string | null;
}

export interface AiQuestionQueue {
  pending: AiQuestionQueueItem[];
  answering: AiQuestionQueueItem[];
  ready: AiQuestionQueueItem[];
  /** 20 câu gần nhất đã xong (Played/Rejected/Expired/Failed). */
  done: AiQuestionQueueItem[];
  /** Câu Played chưa ẩn, mới nhất — nút "Ẩn" tác động đúng câu này. */
  showing: AiQuestionQueueItem | null;
}

/** 1 câu hỏi hiện trên overlay công khai (đã Played, trong 10 phút gần nhất). */
export interface AiQuestionOverlayItem {
  id: string;
  askerName: string;
  question: string;
  answer: string;
  playedAt: string;
}

export interface AiQuestionOverlay {
  items: AiQuestionOverlayItem[];
}

export interface AskAiQuestionBody {
  creatorId: string;
  question: string;
  isAnonymous: boolean;
}

export interface SaveAiQuestionSettingsBody {
  enabled: boolean;
  priceCa: number;
}

const BASE = '/v1/payment-service/ai-question';
const MANAGE_BASE = `${BASE}/manage`;
const PUBLIC_BASE = '/v1/payment-service/public';

// ── Công khai (không cần đăng nhập) ─────────────────────────────────────────────────────

export const getPublicAiQuestionSetting = (userId: string) =>
  API.get<PublicAiQuestionSetting>(`${PUBLIC_BASE}/creator-ai-question-setting`, {
    params: { userId },
  }).then((r) => r.data);

export const getAiQuestionOverlay = (userId: string, key: string) =>
  API.get<AiQuestionOverlay>(`${PUBLIC_BASE}/ai-question-overlay`, {
    params: { userId, key },
  }).then((r) => r.data);

// ── Người xem (đăng nhập) ───────────────────────────────────────────────────────────────

export const askAiQuestion = (body: AskAiQuestionBody) =>
  API.post<AiQuestionMine>(BASE, body).then((r) => r.data);

export const getMyAiQuestions = (creatorId: string) =>
  API.get<AiQuestionMine[]>(`${BASE}/mine`, { params: { creatorId } }).then((r) => r.data);

// ── Streamer (đăng nhập, chủ kênh) ──────────────────────────────────────────────────────

export const getAiQuestionSettings = () =>
  API.get<AiQuestionSetting>(`${MANAGE_BASE}/settings`).then((r) => r.data);

export const saveAiQuestionSettings = (body: SaveAiQuestionSettingsBody) =>
  API.post<AiQuestionSetting>(`${MANAGE_BASE}/settings`, body).then((r) => r.data);

export const rotateAiQuestionKey = () =>
  API.post<AiQuestionSetting>(`${MANAGE_BASE}/settings/rotate-key`).then((r) => r.data);

export const getAiQuestionQueue = () =>
  API.get<AiQuestionQueue>(`${MANAGE_BASE}/queue`).then((r) => r.data);

export const approveAiQuestion = (id: string) =>
  API.post<AiQuestionQueueItem>(`${MANAGE_BASE}/${id}/approve`).then((r) => r.data);

export const rewriteAiQuestion = (id: string) =>
  API.post<AiQuestionQueueItem>(`${MANAGE_BASE}/${id}/rewrite`).then((r) => r.data);

export const rejectAiQuestion = (id: string) =>
  API.post<AiQuestionQueueItem>(`${MANAGE_BASE}/${id}/reject`).then((r) => r.data);

export const playAiQuestion = (id: string) =>
  API.post<AiQuestionQueueItem>(`${MANAGE_BASE}/${id}/play`).then((r) => r.data);

export const hideAiQuestion = () =>
  API.post<AiQuestionQueueItem | null>(`${MANAGE_BASE}/hide`).then((r) => r.data);
